import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SocketService } from '../services/socket.service';
import { SchemeReferenceLoader, type SchemeEntry, type SchemeReferenceData } from './scheme-reference-loader';
import type { LearnedMatch } from './scheme-match';
import { readCachedSchemeReference, writeCachedSchemeReference } from './scheme-reference-cache';

/** Least time between two hash checks when the app comes back into view. */
const CHECK_GAP_MS = 30_000;

/**
 * The scheme / plan / option reference Connect publishes, for clinical's pickers (tasks/scheme-linking-central.md).
 * Checked on start, when the server says it changed, after reconnecting and when the app comes back into view;
 * never on a timer.
 */
@Injectable({ providedIn: 'root' })
export class SchemeReferenceService {
  private readonly referenceSubject = new BehaviorSubject<SchemeReferenceData | null>(null);
  readonly reference$ = this.referenceSubject.asObservable();
  /** The schemes the pickers list; empty until the reference has loaded. */
  get schemes(): SchemeEntry[] {
    return this.referenceSubject.value?.schemes ?? [];
  }
  /** Connect's learned matches for scanned text; none with the bundled fallback. */
  get learned(): LearnedMatch[] {
    return this.referenceSubject.value?.learned ?? [];
  }
  private lastCheck = 0;
  private readonly loader: SchemeReferenceLoader;

  constructor(private http: HttpClient, socketService: SocketService) {
    const headers = () => new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('token')}`);
    this.loader = new SchemeReferenceLoader({
      readCache: () => readCachedSchemeReference(),
      writeCache: (hash, data) => writeCachedSchemeReference(hash, data),
      fetchHash: async () =>
        (await firstValueFrom(this.http.get<{ hash: string | null }>(`${environment.API_URL}/api/scheme-reference/version`, { headers: headers() }))).hash,
      fetchReference: () =>
        firstValueFrom(this.http.get<{ hash: string | null; data: SchemeReferenceData | null }>(`${environment.API_URL}/api/scheme-reference`, { headers: headers() })),
      fetchFallback: () => firstValueFrom(this.http.get<SchemeReferenceData>('assets/medprax-scheme-reference.json')),
      show: (data) => this.referenceSubject.next(data),
    });

    if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) void this.loader.load();
    socketService.listen('schemeReferenceChanged', 'scheme-reference-service').subscribe(() => void this.refresh(true));
    socketService.reconnected$.pipe(filter(Boolean)).subscribe(() => void this.refresh(true));
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.refresh();
      });
    }
  }

  refresh(force = false): Promise<void> {
    if (!force && Date.now() - this.lastCheck < CHECK_GAP_MS) return Promise.resolve();
    if (typeof localStorage === 'undefined' || !localStorage.getItem('token')) return Promise.resolve();
    this.lastCheck = Date.now();
    return this.loader.refresh();
  }
}
